interface JsonLdArticleProps {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  imageUrl?: string;
  authorName?: string;
  publisherName?: string;
  publisherUrl?: string;
}

interface JsonLdWebsiteProps {
  url: string;
  name: string;
  description: string;
  searchUrl?: string;
}

interface JsonLdOrganizationProps {
  url: string;
  name: string;
  description: string;
  logoUrl?: string;
}

interface JsonLdCollectionPageProps {
  url: string;
  name: string;
  description: string;
}

interface JsonLdBreadcrumbProps {
  items: { name: string; url: string }[];
}

interface JsonLdFaqPageProps {
  questions: { question: string; answer: string }[];
}

type JsonLdProps =
  | { type: "Article"; data: JsonLdArticleProps }
  | { type: "WebSite"; data: JsonLdWebsiteProps }
  | { type: "Organization"; data: JsonLdOrganizationProps }
  | { type: "CollectionPage"; data: JsonLdCollectionPageProps }
  | { type: "BreadcrumbList"; data: JsonLdBreadcrumbProps }
  | { type: "FAQPage"; data: JsonLdFaqPageProps }
  | { type: "Multi"; items: { type: string; data: Record<string, unknown> }[] };

function buildArticleLd(data: JsonLdArticleProps): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.headline,
    description: data.description,
    url: data.url,
    datePublished: data.datePublished,
    dateModified: data.dateModified || data.datePublished,
    ...(data.imageUrl ? { image: data.imageUrl } : {}),
    author: {
      "@type": "Organization",
      name: data.authorName || "BaseTao Spreadsheet",
      url: data.publisherUrl || data.url,
    },
    publisher: {
      "@type": "Organization",
      name: data.publisherName || "BaseTao Spreadsheet",
      url: data.publisherUrl || data.url,
      ...(data.imageUrl ? { logo: { "@type": "ImageObject", url: data.imageUrl } } : {}),
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": data.url,
    },
  };
}

function buildWebsiteLd(data: JsonLdWebsiteProps): Record<string, unknown> {
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: data.url,
    name: data.name,
    description: data.description,
  };
  if (data.searchUrl) {
    ld.potentialAction = {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: data.searchUrl,
      },
      "query-input": "required name=search_term_string",
    };
  }
  return ld;
}

function buildOrganizationLd(data: JsonLdOrganizationProps): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    url: data.url,
    name: data.name,
    description: data.description,
    ...(data.logoUrl ? { logo: data.logoUrl } : {}),
  };
}

function buildCollectionPageLd(data: JsonLdCollectionPageProps): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    url: data.url,
    name: data.name,
    description: data.description,
  };
}

function buildBreadcrumbLd(data: JsonLdBreadcrumbProps): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: data.items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function buildFaqPageLd(data: JsonLdFaqPageProps): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.questions.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };
}

export default function SeoJsonLd(props: JsonLdProps) {
  let ld: Record<string, unknown> | Record<string, unknown>[];

  if (props.type === "Multi") {
    const builders: Record<string, (d: Record<string, unknown>) => Record<string, unknown>> = {
      Article: (d) => buildArticleLd(d as unknown as JsonLdArticleProps),
      WebSite: (d) => buildWebsiteLd(d as unknown as JsonLdWebsiteProps),
      Organization: (d) => buildOrganizationLd(d as unknown as JsonLdOrganizationProps),
      CollectionPage: (d) => buildCollectionPageLd(d as unknown as JsonLdCollectionPageProps),
      BreadcrumbList: (d) => buildBreadcrumbLd(d as unknown as JsonLdBreadcrumbProps),
      FAQPage: (d) => buildFaqPageLd(d as unknown as JsonLdFaqPageProps),
    };
    ld = props.items.map((item) => {
      const builder = builders[item.type];
      if (!builder) return item.data;
      return builder(item.data);
    });
  } else {
    switch (props.type) {
      case "Article":
        ld = buildArticleLd(props.data);
        break;
      case "WebSite":
        ld = buildWebsiteLd(props.data);
        break;
      case "Organization":
        ld = buildOrganizationLd(props.data);
        break;
      case "CollectionPage":
        ld = buildCollectionPageLd(props.data);
        break;
      case "BreadcrumbList":
        ld = buildBreadcrumbLd(props.data);
        break;
      case "FAQPage":
        ld = buildFaqPageLd(props.data);
        break;
    }
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
    />
  );
}